import { useMemo, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  isWithinInterval,
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faYoutube,
  faTwitter,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import { faCloud } from '@fortawesome/free-solid-svg-icons';
import { formatLocalized } from '@/utils/dateFormat';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage } from '@/components/ui';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { Post } from '@/services/posts';
import { useCalendarPosts, useMultiCalendarPosts } from '@/hooks/useCalendarPosts';
import { getInferredStatus, getStatusIcon } from '@/utils/calendarStatus';
import { useCalendarNotes } from '@/hooks/useCalendarNotes';
import { useCalendarHolidays } from '@/hooks/useCalendarHolidays';
import { useTimezone } from '@/contexts/TimezoneContext';
import { CalendarNavigation } from './CalendarNavigation';
import { CalendarView } from '@/types/calendar';
import { CalendarNote } from '@/types/calendar-notes';
import { CalendarHoliday } from '@/types/holidays';
import { filterCampaignsForDay, getCampaignDayPosition, assignCampaignSlots, CampaignDayPosition } from '@/utils/campaignMatcher';
import { getContrastColor } from '@/utils/colors';
import { timeSync, recordSlowOp } from '@/utils/perfTelemetry';
import { useCalendarSettingsStore } from '@/stores/calendarSettingsStore';
import { MoonPhaseIndicator } from './MoonPhase';
import { QuickAddMenu } from './QuickAddMenu';
import { CalendarPostPopover } from './PostPopover';

/**
 * CalendarWeekView Component
 *
 * Hobonichi-style week view with 7 equal columns.
 * Clean, content-first design with generous spacing.
 */

interface CalendarWeekViewProps {
  /**
   * Current view mode
   */
  view: CalendarView;

  /**
   * Callback when view changes
   */
  onViewChange: (view: CalendarView) => void;

  /**
   * Week containing this date
   */
  currentDate: Date;

  /**
   * Navigate to different week
   */
  onDateChange: (date: Date) => void;

  /**
   * Click on a day
   */
  onDateSelect: (date: Date, posts: Post[]) => void;

  /**
   * Calendar IDs for multi-calendar support
   */
  calendarIds: number[];

  /**
   * Platform filter
   */
  platform?: string;

  /**
   * Global drag state
   */
  isDraggingActive?: boolean;

  /**
   * Drag-drop handler
   */
  onPostRescheduled?: (postId: number, newDate: Date) => void;

  /**
   * Click on campaign to edit
   */
  onCampaignClick?: (campaign: CalendarNote) => void;

  /**
   * Callback when add post button is clicked
   */
  onAddPost?: (date: Date) => void;

  /**
   * Callback when add campaign button is clicked
   */
  onAddCampaign?: (date: Date) => void;

  /**
   * Callback when add content pillar button is clicked
   */
  onAddContentPillar?: (date: Date) => void;

  /**
   * Callback when a post card is clicked directly - opens editor without the day drawer
   */
  onPostClick?: (post: Post) => void;

  /**
   * Callback when a post delete action is triggered from the popover
   */
  onPostDelete?: (post: Post) => void;

  /**
   * Map of calendarId → { name, color } for tooltip display
   */
  calendarsById?: Map<number, { name: string; color: string }>;
}

/**
 * Compact Post Card for Week View (draggable)
 */
interface WeekPostCardProps {
  post: Post;
  date: Date;
  onClick: () => void;
  isDraggingGlobal?: boolean;
  onPostClick?: (post: Post) => void;
  onPostDelete?: (post: Post) => void;
}

const WeekPostCard = ({ post, date, onClick, isDraggingGlobal = false, onPostClick, onPostDelete }: WeekPostCardProps) => {
  const { formatTimeOnly } = useTimezone();
  const navigate = useNavigate();

  const isBlog = post.source === 'blog';
  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia?.originalUrl;
  const time = post.scheduledAt ? formatTimeOnly(post.scheduledAt) : '';
  const statusIcon = getStatusIcon(post);
  const inferredStatus = getInferredStatus(post);

  // Allow dragging for MAPPED (not yet committed) and SCHEDULED (committed but not yet published)
  // In-flight (PUBLISHING) and completed (PUBLISHED, FAILED) posts cannot be moved
  // Blog entries are READ-ONLY: never draggable
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // mappingId is set per-entry from calendar endpoint, ensuring uniqueness for multi-mapped posts
  const mappingId = post.mappingId || post.calendarMappings?.[0]?.id;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // Use mappingId in ID to ensure uniqueness when same post has multiple mappings.
    // Blog entries use a namespaced id so they never collide with a real social post's id.
    id: isBlog ? `blog-post-${post.blogPostId}` : `calendar-post-${mappingId || post.id}`,
    data: {
      type: 'calendar-post',
      post,
      mappingId, // Include specific mappingId for move operations
      sourceDate: date.toISOString(),
    },
    disabled: !canDrag || isBlog,
  });

  // Blog chips click straight through to the blog post, bypassing the day-drawer/edit flow
  const handleClick = () => {
    if (isBlog) {
      navigate(`/blog/${post.blogPostId}`);
      return;
    }
    onClick();
  };

  return (
    <HoverCard
      trigger={
        <div
          ref={setNodeRef}
          {...(canDrag ? { ...listeners, ...attributes } : {})}
          onClick={handleClick}
          className={cn(
            "w-full text-left bg-white rounded-lg p-2 border border-gray-200 transition-all group",
          canDrag && !isDragging && "cursor-grab hover:border-gray-300 hover:shadow-sm",
          !canDrag && (isBlog || inferredStatus === 'MAPPED') && "cursor-pointer hover:border-gray-300 hover:shadow-sm",
          !canDrag && !isBlog && inferredStatus !== 'MAPPED' && "cursor-default",
          isDragging && "opacity-50 cursor-grabbing",
          isDraggingGlobal && !isDragging && "opacity-70"
        )}
        role="button"
        tabIndex={0}
      >
        {/* Square Media thumbnail with status and time */}
        <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2">
          {hasMedia ? (
            <RetryImage
              src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <PlaceholderImage />
          )}
          {post.postMedia && post.postMedia.length > 1 && (
            <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] text-white font-medium">
              +{post.postMedia.length - 1}
            </div>
          )}
          {/* Blog badge (top-left) — distinguishes read-only WordPress blog chips from social posts */}
          {isBlog && (
            <div className="absolute top-1 left-1 px-1 py-0.5 bg-white/90 rounded flex items-center justify-center">
              <Icon name={ICONS.BLOG} size="xs" className="text-gray-600" ariaHidden />
            </div>
          )}
          {/* Bottom bar with status icon and time */}
          <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[10px] py-1 px-1.5 flex items-center justify-between">
            <Icon name={statusIcon.icon} size="xs" className="text-white" ariaHidden />
            {time && <span className="font-medium">{time}</span>}
          </div>
        </div>

        {/* Caption preview */}
        <p className="hidden lg:block text-xs text-gray-700 line-clamp-2 mb-2 whitespace-pre-wrap">
          {post.caption.substring(0, 60)}
          {post.caption.length > 60 ? '...' : ''}
        </p>

        {/* Platforms */}
        {post.postChannels && post.postChannels.length > 0 && (
          <div className="hidden lg:flex gap-1 flex-wrap">
            {post.postChannels.slice(0, 3).map((pc) => {
              const platform = pc.channel.platform;
              const profilePhotoUrl = pc.channel.profilePhotoUrl;

              // Platform icons mapping (same as CalendarDayPosts)
              const platformIcons: Record<string, IconDefinition> = {
                FACEBOOK: faFacebook,
                INSTAGRAM: faInstagram,
                LINKEDIN: faLinkedin,
                TWITTER: faTwitter,
                YOUTUBE: faYoutube,
                GOOGLE_MY_BUSINESS: faGoogle,
                PINTEREST: faPinterest,
                TIKTOK: faTiktok,
                THREADS: faThreads,
                BLUESKY: faCloud,
              };

              return (
                <div key={pc.id} className="relative">
                  <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center ring-1 ring-gray-200 bg-white">
                    {profilePhotoUrl ? (
                      <img src={profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <FontAwesomeIcon
                        icon={platformIcons[platform] || faCloud}
                        className="text-gray-500 text-xs"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </div>
              );
            })}
            {post.postChannels.length > 3 && (
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] text-gray-500 font-medium ring-1 ring-gray-200">
                +{post.postChannels.length - 3}
              </div>
            )}
          </div>
        )}
      </div>
      }
      side="left"
      align="start"
      sideOffset={8}
    >
      <CalendarPostPopover
        post={post}
        inferredStatus={inferredStatus}
        onEdit={onPostClick}
        onDelete={onPostDelete}
      />
    </HoverCard>
  );
};

/**
 * Day Column Component
 */
interface DayColumnProps {
  date: Date;
  posts: Post[];
  isToday: boolean;
  isDraggingActive: boolean;
  onPostClick: (post: Post) => void;
  onPostDelete?: (post: Post) => void;
  campaigns?: CalendarNote[];
  campaignPositions?: Map<number, CampaignDayPosition>;
  campaignSlots?: Map<number, number>;
  holidays?: CalendarHoliday[];
  onCampaignClick?: (campaign: CalendarNote) => void;
  onAddPost?: (date: Date) => void;
  onAddCampaign?: (date: Date) => void;
  onAddContentPillar?: (date: Date) => void;
  calendarsById?: Map<number, { name: string; color: string }>;
}

const DayColumn = ({
  date,
  posts,
  isToday,
  isDraggingActive,
  onPostClick,
  onPostDelete,
  campaigns = [],
  campaignPositions,
  campaignSlots,
  holidays = [],
  onCampaignClick,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
  calendarsById,
}: DayColumnProps) => {
  const { t } = useTranslation('common');
  const { showMoonPhases } = useCalendarSettingsStore();
  const dateString = format(date, 'yyyy-MM-dd');

  const { isOver, setNodeRef } = useDroppable({
    id: `week-day-${dateString}`,
    data: {
      type: 'calendar-day',
      date: date.toISOString(),
    },
  });

  return (
    <div
      ref={setNodeRef}
      data-calendar-day={dateString}
      className={cn(
        'group relative flex flex-row lg:flex-col border-b lg:border-b-0 lg:border-r border-gray-200 last:border-b-0 lg:last:border-r-0 min-h-[80px] lg:min-h-0',
        isDraggingActive && 'transition-all',
        isToday && 'bg-primary-50/50',
        isOver && 'bg-primary-100'
      )}
    >
      {/* Day Label — left strip on mobile, top header on desktop */}
      <div
        className={cn(
          'flex-shrink-0 border-r lg:border-r-0 lg:border-b border-gray-200 p-2 lg:py-1.5',
          'flex flex-col items-center justify-center w-8 lg:w-auto',
          'lg:sticky lg:top-0 lg:z-10',
          isToday ? 'bg-primary-50' : 'bg-gray-50 lg:bg-white'
        )}
      >
        <span className={cn(
          'text-[10px] font-semibold uppercase leading-tight',
          isToday ? 'text-primary-600' : 'text-gray-500'
        )}>
          {formatLocalized(date, 'EEE')}
          {posts.length > 0 && (
            <span className={cn('hidden lg:inline ml-1', isToday ? 'text-primary-400' : 'text-gray-400')}>
              ({posts.length})
            </span>
          )}
        </span>
        <span className={cn(
          'text-sm font-bold leading-tight',
          isToday ? 'text-primary-600' : 'text-gray-900'
        )}>
          {format(date, 'd')}
        </span>
        {posts.length > 0 && (
          <span className={cn('lg:hidden text-[10px]', isToday ? 'text-primary-400' : 'text-gray-400')}>
            {posts.length}
          </span>
        )}
        {showMoonPhases && (
          <MoonPhaseIndicator date={date} size="sm" className="opacity-70 mt-0.5" />
        )}
        <div className="mt-1">
          <QuickAddMenu
            date={date}
            onAddPost={onAddPost}
            onAddCampaign={onAddCampaign}
            onAddContentPillar={onAddContentPillar}
            menuPosition="left"
          />
        </div>
      </div>

      {/* Content area: campaigns + posts (fills remaining width on mobile) */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">

      {/* Campaigns and Holidays */}
      {(campaigns.length > 0 || holidays.length > 0) && (
        <div className="px-2 pt-2 space-y-1 flex-shrink-0">
          {(() => {
            if (campaigns.length === 0) return null;

            const maxSlot = campaigns.reduce((max, c) => {
              const slot = campaignSlots?.get(c.id) ?? 0;
              return slot > max ? slot : max;
            }, 0);

            const slotArray: Array<CalendarNote | null> = new Array(maxSlot + 1).fill(null);
            for (const c of campaigns) {
              const slot = campaignSlots?.get(c.id) ?? 0;
              slotArray[slot] = c;
            }

            return slotArray.map((campaign, slotIdx) => {
              if (!campaign) {
                return <div key={`spacer-${slotIdx}`} className="text-xs py-1 invisible">{'\u00A0'}</div>;
              }

              const position = campaignPositions?.get(campaign.id) ?? 'single';
              const isFirst = position === 'first' || position === 'single';
              const isLast = position === 'last' || position === 'single';
              const isMiddle = position === 'middle';

              return (
                <Tooltip
                  key={campaign.id}
                  content={
                    <div>
                      <div>
                        <span className="font-medium">{campaign.repeatType ? t('content_pillar', { defaultValue: 'Content Pillar' }) : t('campaign')}:</span> {campaign.title}
                        {campaign.repeatType && (
                          <span className="text-gray-300 ml-1">
                            ({t(`repeat_${campaign.repeatType.toLowerCase()}`)})
                          </span>
                        )}
                      </div>
                      {campaign.description && (
                        <div className="text-gray-300 text-xs mt-1">{campaign.description}</div>
                      )}
                      {calendarsById?.get(campaign.calendarId)?.name && (
                        <div className="text-gray-400 text-xs mt-0.5">{calendarsById.get(campaign.calendarId)?.name}</div>
                      )}
                    </div>
                  }
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onCampaignClick?.(campaign);
                    }}
                    className={cn(
                      'text-xs py-1 font-medium text-left truncate transition-opacity',
                      isFirst && isLast && 'rounded px-1.5',
                      isFirst && !isLast && 'rounded-l px-1.5 -mr-2',
                      isLast && !isFirst && 'rounded-r px-1.5 -ml-2',
                      isMiddle && 'rounded-none px-1.5 -mx-2',
                      onCampaignClick && 'cursor-pointer hover:opacity-80'
                    )}
                    style={{
                      backgroundColor: campaign.color || '#3B82F6',
                      color: getContrastColor(campaign.color || '#3B82F6'),
                    }}
                  >
                    {campaign.title}
                  </div>
                </Tooltip>
              );
            });
          })()}

          {holidays.map((holiday) => (
            <Tooltip
              key={holiday.id}
              content={
                <div>
                  <span className="font-medium">{t('holiday')}:</span> {holiday.localName}
                  <span className="text-gray-300 ml-1">({holiday.countryName})</span>
                </div>
              }
            >
              <div
                className="text-xs px-1.5 py-1 rounded truncate font-medium text-left"
                style={{
                  backgroundColor: holiday.colorCode || '#FFD700',
                  color: getContrastColor(holiday.colorCode || '#FFD700'),
                }}
              >
                {holiday.localName}
              </div>
            </Tooltip>
          ))}
        </div>
      )}

      {/* Posts */}
      <div
        className={cn(
          'flex-1 min-h-0 p-2 overflow-y-auto',
          'grid grid-cols-3 gap-1.5 lg:flex lg:flex-col lg:gap-0 lg:space-y-2',
          isToday ? 'bg-primary-50/30' : 'bg-gray-50',
          '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]'
        )}
      >
        {posts.length === 0 ? (
          isDraggingActive ? (
            <div className="text-center py-8 text-xs text-gray-400">
              {t('drop_here')}
            </div>
          ) : null
        ) : (
          posts.map((post) => (
            <WeekPostCard
              key={post.source === 'blog' ? `blog-${post.blogPostId}` : `${post.id}-${post.mappingId || 'unmapped'}`}
              post={post}
              date={date}
              onClick={() => onPostClick(post)}
              isDraggingGlobal={isDraggingActive}
              onPostClick={onPostClick}
              onPostDelete={onPostDelete}
            />
          ))
        )}
      </div>

      </div>{/* end content area */}

      {/* Drop zone indicator */}
      {isOver && (
        <div className="absolute inset-0 border-2 border-dashed border-primary-500 bg-primary-50/50 pointer-events-none rounded-lg" />
      )}
    </div>
  );
};

export const CalendarWeekView = ({
  view,
  onViewChange,
  currentDate,
  onDateChange,
  onDateSelect,
  calendarIds,
  platform,
  isDraggingActive = false,
  onCampaignClick,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
  onPostClick,
  onPostDelete,
  calendarsById,
}: CalendarWeekViewProps) => {
  const __renderStart = performance.now();
  useLayoutEffect(() => {
    recordSlowOp('render:CalendarWeekView', performance.now() - __renderStart);
  });
  const { t } = useTranslation(['common', 'calendar']);
  const { timezone } = useTimezone();

  // "today" anchored at noon in team timezone so isSameDay comparisons are correct
  const zonedNow = toZonedTime(new Date(), timezone);
  const todayAnchor = fromZonedTime(
    `${zonedNow.getFullYear()}-${String(zonedNow.getMonth() + 1).padStart(2, '0')}-${String(zonedNow.getDate()).padStart(2, '0')}T12:00:00`,
    timezone
  );

  // Calculate week boundaries in team timezone
  const zonedCurrent = toZonedTime(currentDate, timezone);
  const weekStart = startOfWeek(zonedCurrent, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(zonedCurrent, { weekStartsOn: 1 });

  // Stable string key for memo — 'yyyy-MM-dd' of the Monday in team tz
  const weekStartKey = format(weekStart, 'yyyy-MM-dd');

  // Generate week days — each anchored at noon in team timezone
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    let cursor = weekStart;
    for (let i = 0; i < 7; i++) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, '0');
      const d = String(cursor.getDate()).padStart(2, '0');
      days.push(fromZonedTime(`${y}-${m}-${d}T12:00:00`, timezone));
      cursor = addDays(cursor, 1);
    }
    return days;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartKey, timezone]);

  // Fetch posts for the week
  const weekMonth = format(weekStart, 'yyyy-MM');
  const weekEndMonth = format(weekEnd, 'yyyy-MM');
  const spansTwoMonths = weekMonth !== weekEndMonth;
  const shouldUseMultiCalendar = calendarIds && calendarIds.length > 0;

  const multiCalendarResult = useMultiCalendarPosts({
    month: weekMonth,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar,
  });

  const singleCalendarResult = useCalendarPosts({
    month: weekMonth,
    platform,
    enabled: !shouldUseMultiCalendar,
  });

  // Second month hooks — only active when the week spans two months
  const multiCalendarResult2 = useMultiCalendarPosts({
    month: weekEndMonth,
    calendarIds: calendarIds || [],
    platform,
    enabled: shouldUseMultiCalendar && spansTwoMonths,
  });

  const singleCalendarResult2 = useCalendarPosts({
    month: weekEndMonth,
    platform,
    enabled: !shouldUseMultiCalendar && spansTwoMonths,
  });

  const primaryResult = shouldUseMultiCalendar ? multiCalendarResult : singleCalendarResult;
  const secondaryResult = shouldUseMultiCalendar ? multiCalendarResult2 : singleCalendarResult2;

  const isLoading = primaryResult.isLoading || (spansTwoMonths && secondaryResult.isLoading);

  // Namespaced identity for dedup — a blog entry's `id` (its blogPostId) can numerically
  // collide with an unrelated social post's `id`, so blog entries must never be deduped
  // against (or mistaken for) a social entry sharing the same number.
  const postIdentity = (p: Post): string => (p.source === 'blog' ? `blog-${p.blogPostId}` : `post-${p.id}`);

  const getPostsForDate = (date: string): Post[] => {
    const primary = primaryResult.getPostsForDate(date);
    if (spansTwoMonths) {
      const secondary = secondaryResult.getPostsForDate(date);
      if (secondary.length > 0) {
        const seen = new Set(primary.map(postIdentity));
        return [...primary, ...secondary.filter(p => !seen.has(postIdentity(p)))];
      }
    }
    return primary;
  };

  const refetch = () => {
    primaryResult.refetch();
    if (spansTwoMonths) secondaryResult.refetch();
  };

  // Fetch campaigns and holidays for the week
  const firstCalendarId = calendarIds && calendarIds.length > 0 ? calendarIds[0] : undefined;

  const { data: campaigns } = useCalendarNotes(
    firstCalendarId || 0,
    format(weekStart, 'yyyy-MM-dd'),
    format(weekEnd, 'yyyy-MM-dd')
  );

  const { data: holidays } = useCalendarHolidays(
    firstCalendarId || 0,
    format(weekStart, 'yyyy-MM-dd'),
    format(weekEnd, 'yyyy-MM-dd')
  );

  // Compute stable slot indices for campaigns across the week
  const campaignSlots = useMemo(
    () => timeSync('assignCampaignSlots', () => assignCampaignSlots(campaigns, weekDays)),
    [campaigns, weekDays]
  );

  // Navigation
  const handlePrevWeek = () => {
    onDateChange(subWeeks(currentDate, 1));
  };

  const handleNextWeek = () => {
    onDateChange(addWeeks(currentDate, 1));
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const handlePostClick = (date: Date, post: Post) => {
    // Only MAPPED posts can be clicked to edit directly (same as drawer's canEdit)
    const inferredStatus = getInferredStatus(post);
    if (onPostClick && inferredStatus === 'MAPPED') {
      // Direct edit - skip the day drawer entirely
      onPostClick(post);
    } else {
      // Non-editable post or no handler: open the day drawer with all posts for the day
      const dateString = format(date, 'yyyy-MM-dd');
      const allDayPosts = getPostsForDate(dateString);
      onDateSelect(date, allDayPosts);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Unified Calendar Navigation */}
      <CalendarNavigation
        view={view}
        onViewChange={onViewChange}
        currentDate={currentDate}
        onPrev={handlePrevWeek}
        onNext={handleNextWeek}
        onToday={handleToday}
        title={`${format(weekStart, 'dd.MM.yyyy')} – ${format(weekEnd, 'dd.MM.yyyy')}`}
        hideTodayButton={isWithinInterval(todayAnchor, { start: weekStart, end: weekEnd })}
        onRefresh={refetch}
        isLoading={isLoading}
      />

      {/* Loading State */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-600">
            <Icon name={ICONS.LOADER} size="md" className="animate-spin" ariaHidden />
            <span>{t('loading_week')}</span>
          </div>
        </div>
      )}

      {/* Week Grid */}
      {!isLoading && (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-7 min-h-0">
          {weekDays.map((day) => {
            const dateString = format(day, 'yyyy-MM-dd');
            const posts = getPostsForDate(dateString);
            const isToday = isSameDay(day, todayAnchor);

            // Filter campaigns for this day (handles recurring campaigns)
            const dayCampaigns = filterCampaignsForDay(campaigns, day);

            // Compute position for each campaign on this day
            const dayPositions = new Map<number, CampaignDayPosition>();
            for (const c of dayCampaigns) {
              dayPositions.set(c.id, getCampaignDayPosition(c, day));
            }

            // Filter holidays for this day
            const dayHolidays = holidays?.filter((h) =>
              isSameDay(new Date(h.holidayDate), day)
            );

            return (
              <DayColumn
                key={dateString}
                date={day}
                posts={posts}
                isToday={isToday}
                isDraggingActive={isDraggingActive}
                onPostClick={(post) => handlePostClick(day, post)}
                onPostDelete={onPostDelete}
                campaigns={dayCampaigns}
                campaignPositions={dayPositions}
                campaignSlots={campaignSlots}
                holidays={dayHolidays}
                onCampaignClick={onCampaignClick}
                onAddPost={onAddPost}
                onAddCampaign={onAddCampaign}
                onAddContentPillar={onAddContentPillar}
                calendarsById={calendarsById}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
